package com.dotdrives.web.pages.donations

import com.dotdrives.controller.QuickRecordController
import com.dotdrives.controller.QuickRecordUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.Target
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface QuickRecordDialogProps : Props {
    var controller: QuickRecordController
    var state: QuickRecordUiState
    var targets: List<Target>
    var onCancel: () -> Unit
    var onSaved: () -> Unit
    var onSubmit: (() -> Unit)?
    var completesDriveMove: Boolean?
    var submitting: Boolean?
    var externalError: String?
    var onRetryWorkflow: (() -> Unit)?
}

val QuickRecordDialog = FC<QuickRecordDialogProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "quick-record-dialog", props.onCancel)
    val state = props.state
    val controller = props.controller
    val submit = props.onSubmit ?: controller::save

    react.useEffect(state.savedDonation?.id) {
        if (state.savedDonation != null) props.onSaved()
    }

    div {
        className = ClassName("donations-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            className = ClassName("donations-quick-dialog")
            id = web.dom.ElementId("quick-record-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Record a gift"
            onClick = { it.stopPropagation() }
            onKeyDown = { event ->
                // Enter submits the dialog (design: "Save gift · ⏎") unless focus is in the
                // multiline memo field, where Enter has to keep inserting a newline.
                val targetTag = event.target.asDynamic().tagName as? String
                // A focused Cancel/close <button>'s own Enter activation bubbles here too;
                // without excluding BUTTON, tabbing to Cancel and pressing Enter both fires
                // the button's click (dismissing) and this handler's save() -- writing an
                // insert-only donation row the user was trying to abandon.
                if (event.key == "Enter" && targetTag != "TEXTAREA" && targetTag != "BUTTON" &&
                    state.selectedGiver != null && state.amountText.isNotBlank() && !(state.saving || props.submitting == true)
                ) {
                    event.preventDefault()
                    submit()
                }
            }

            div {
                className = ClassName("donations-quick-head")
                div { className = ClassName("donations-quick-title"); +"Record a gift" }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("donations-quick-close")
                    ariaLabel = "Cancel"
                    onClick = { props.onCancel() }
                    +"✕"
                }
            }
            div {
                className = ClassName("donations-quick-subtitle")
                +when {
                    props.completesDriveMove == true -> "Record the actual gift for this giver and drive. Saving also completes the drive move."
                    state.contextLocked -> "Record a gift for this giver and drive. The drive stays open."
                    else -> "Two fields. We prefill the rest from what we know — change anything."
                }
            }

            props.externalError?.let { error -> DotBanner { message = error; onRetry = props.onRetryWorkflow } }
            state.error?.let { error -> DotBanner { message = error; className = "donations-inline-banner" } }

            if (props.onRetryWorkflow != null) {
                div { className = ClassName("donations-quick-note"); +"The original gift details are saved. Retry reuses them; finish this request before starting another gift." }
                if (props.externalError == null) DotButton {
                    text = "Retry unfinished gift"
                    onClick = props.onRetryWorkflow!!
                }
            }
            react.dom.html.ReactHTML.fieldset {
                className = ClassName("donations-gift-fields")
                disabled = state.saving || props.submitting == true
                div {
                    className = ClassName("donations-quick-fields")
                    div {
                        div { className = ClassName("donations-quick-field-label"); +"Giver" }
                        div {
                            className = ClassName("donations-quick-giver-search")
                            if (state.selectedGiver == null) {
                                input {
                                    className = ClassName("donations-quick-giver-input")
                                    type = InputType.text
                                    value = state.giverQuery
                                    placeholder = "Search givers…"
                                    ariaLabel = "Search givers"
                                    onChange = { controller.searchGiver(it.currentTarget.value) }
                                }
                                if (state.giverQuery.isNotBlank() && state.giverResults.isNotEmpty()) {
                                    div {
                                        className = ClassName("donations-quick-giver-results")
                                        state.giverResults.forEach { giver ->
                                            val (bg, fg) = DotAvatarPalette.tintFor(giver.id)
                                            val avatarStyle = js("({})")
                                            avatarStyle.backgroundColor = bg
                                            avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                                            button {
                                                key = giver.id.unsafeCast<Key>()
                                                type = ButtonType.buttonType
                                                className = ClassName("donations-quick-giver-result")
                                                onClick = { controller.selectGiver(giver) }
                                                div {
                                                    className = ClassName("donations-row-avatar")
                                                    style = avatarStyle
                                                    +donationInitials(giver.displayName)
                                                }
                                                +giver.displayName
                                            }
                                        }
                                    }
                                }
                            } else {
                                val giver = state.selectedGiver!!
                                val (bg, fg) = DotAvatarPalette.tintFor(giver.id)
                                val avatarStyle = js("({})")
                                avatarStyle.backgroundColor = bg
                                avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                                div {
                                    className = ClassName("donations-quick-selected-giver")
                                    div {
                                        className = ClassName("donations-row-avatar")
                                        style = avatarStyle
                                        +donationInitials(giver.displayName)
                                    }
                                    +giver.displayName
                                    if (!state.contextLocked) button {
                                        type = ButtonType.buttonType
                                        className = ClassName("donations-quick-close")
                                        ariaLabel = "Change giver"
                                        onClick = { controller.searchGiver("") }
                                        +"✕"
                                    }
                                }
                            }
                        }
                    }
                    div {
                        div { className = ClassName("donations-quick-field-label"); +"Amount" }
                        input {
                            className = ClassName("donations-quick-amount-input")
                            type = InputType.text
                            value = state.amountText
                            placeholder = "$ 0"
                            ariaLabel = "Amount"
                            onChange = { controller.setAmount(it.currentTarget.value) }
                        }
                    }
                }

                if (state.selectedGiver != null) {
                    div {
                        className = ClassName("donations-quick-defaults")
                        div { className = ClassName("donations-quick-defaults-label"); +"⚡ PREFILLED FROM CONTEXT" }
                        div {
                            className = ClassName("donations-quick-chip-row")
                            val defaults = state.defaults
                            span { className = ClassName("donations-quick-chip"); +"🎯 ${defaults?.targetName ?: "No target"}" }
                            defaults?.driveTitle?.let { title ->
                                span { className = ClassName("donations-quick-chip"); +"🧭 Matches $title ✓" }
                            }
                            span { className = ClassName("donations-quick-chip"); +"📅 Today" }
                            span { className = ClassName("donations-quick-chip"); +"💳 ${state.paymentType.replaceFirstChar(Char::uppercase)}" }
                        }
                        val defaults = state.defaults
                        if (defaults?.pledgeId != null) {
                            div {
                                className = ClassName("donations-quick-note")
                                +"Saving will mark the "
                                b { +formatMoneyFull(defaults.pledgeAmount) }
                                +" pledge on ${defaults.driveTitle ?: "this drive"} as received. 💛"
                            }
                        }
                    }
                }

                if (state.moreOptionsOpen) {
                    div {
                        className = ClassName("donations-quick-more")
                        div {
                            div { className = ClassName("donations-quick-field-label"); +"Donation target" }
                            select {
                                ariaLabel = "Donation target"
                                value = state.targetId ?: ""
                                onChange = { controller.setTargetId(it.currentTarget.value.takeIf(String::isNotBlank)) }
                                option { value = ""; +"No target" }
                                props.targets.forEach { target ->
                                    option { key = target.id.unsafeCast<Key>(); value = target.id; +target.name }
                                }
                            }
                        }
                        div {
                            div { className = ClassName("donations-quick-field-label"); +"Payment type" }
                            select {
                                ariaLabel = "Payment type"
                                value = state.paymentType
                                if (state.contextLocked) option { value = ""; +"Choose payment type" }
                                onChange = { controller.setPaymentType(it.currentTarget.value) }
                                listOf("cash", "check", "ach", "card", "wire", "other").forEach { type ->
                                    option { key = type.unsafeCast<Key>(); value = type; +type.replaceFirstChar(Char::uppercase) }
                                }
                            }
                        }
                        div {
                            div { className = ClassName("donations-quick-field-label"); +"Memo" }
                            textarea {
                                ariaLabel = "Memo"
                                value = state.memo
                                onChange = { controller.setMemo(it.currentTarget.value) }
                            }
                        }
                    }
                }

                button {
                    type = ButtonType.buttonType
                    className = ClassName("donations-quick-more-toggle")
                    onClick = { controller.toggleMoreOptions() }
                    +(if (state.moreOptionsOpen) "Hide options" else "More options ▾")
                }

            }
            div {
                className = ClassName("donations-quick-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !(state.saving || props.submitting == true)
                }
                DotButton {
                    text = "Save gift · ⏎"
                    className = "donations-quick-save"
                    onClick = submit
                    loading = state.saving
                    enabled = state.selectedGiver != null && state.amountText.isNotBlank() && !(state.saving || props.submitting == true)
                }
            }
        }
    }
}
